import React, { useEffect, useState } from 'react';
import api from '../../services/api';
import { ProductMetrics, ProductOptionMetrics } from '../../types';
import './AdminMetrics.css';

const AdminMetrics: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [productTotals, setProductTotals] = useState<ProductMetrics[]>([]);
  const [productOptions, setProductOptions] = useState<ProductOptionMetrics[]>([]);
  const [customPrintOptions, setCustomPrintOptions] = useState<ProductOptionMetrics[]>([]);

  useEffect(() => {
    const fetchMetrics = async (): Promise<void> => {
      try {
        const response = await api.get<{
          productTotals: ProductMetrics[];
          productOptionTotals: ProductOptionMetrics[];
          customPrintOptionTotals: ProductOptionMetrics[];
        }>('/metrics/admin');
        setProductTotals(response.data.productTotals || []);
        setProductOptions(response.data.productOptionTotals || []);
        setCustomPrintOptions(response.data.customPrintOptionTotals || []);
      } catch (error) {
        console.error('Failed to fetch metrics:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchMetrics();
  }, []);

  if (loading) {
    return <div className="loading">Loading metrics...</div>;
  }

  return (
    <div className="admin-metrics">
      <div className="container">
        <h1>Sales Metrics</h1>

        <div className="metrics-card">
          <h2>Product Totals</h2>
          {productTotals.length === 0 ? (
            <p>No product metrics yet.</p>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Custom</th>
                  <th>Viewed</th>
                  <th>Selected</th>
                  <th>Carted</th>
                  <th>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {productTotals.map((item) => (
                  <tr key={item.productId}>
                    <td>{item.name}</td>
                    <td>{item.isCustomPrint ? 'Yes' : 'No'}</td>
                    <td>{item.viewed}</td>
                    <td>{item.selected}</td>
                    <td>{item.carted}</td>
                    <td>{item.ordered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="metrics-card">
          <h2>Product Options</h2>
          {productOptions.length === 0 ? (
            <p>No option metrics yet.</p>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Product ID</th>
                  <th>Size</th>
                  <th>Material</th>
                  <th>Finish</th>
                  <th>Viewed</th>
                  <th>Selected</th>
                  <th>Carted</th>
                  <th>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {productOptions.map((item, index) => (
                  <tr key={`${item.productId}-${item.size}-${index}`}>
                    <td>{item.productId}</td>
                    <td>{item.size || '—'}</td>
                    <td>{item.material || '—'}</td>
                    <td>{item.finish || '—'}</td>
                    <td>{item.viewed}</td>
                    <td>{item.selected}</td>
                    <td>{item.carted}</td>
                    <td>{item.ordered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="metrics-card">
          <h2>Custom Print Options</h2>
          {customPrintOptions.length === 0 ? (
            <p>No custom print metrics yet.</p>
          ) : (
            <table className="metrics-table">
              <thead>
                <tr>
                  <th>Size</th>
                  <th>Material</th>
                  <th>Finish</th>
                  <th>Viewed</th>
                  <th>Selected</th>
                  <th>Carted</th>
                  <th>Ordered</th>
                </tr>
              </thead>
              <tbody>
                {customPrintOptions.map((item, index) => (
                  <tr key={`${item.size}-${item.material}-${index}`}>
                    <td>{item.size || '—'}</td>
                    <td>{item.material || '—'}</td>
                    <td>{item.finish || '—'}</td>
                    <td>{item.viewed}</td>
                    <td>{item.selected}</td>
                    <td>{item.carted}</td>
                    <td>{item.ordered}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminMetrics;
